import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import {
    CompanyEmployeeActionFailure,
    CompanyEmployeeOverviewPageParams,
    CreateCompanyEmployee,
    CreateCompanyEmployeeSuccess,
    DeleteCompanyEmployee,
    DeleteCompanyEmployeeSuccess,
    EmployeeFilterCounts,
    LoadCompanyEmployeeDetailsByCompany,
    LoadCompanyEmployeeDetailsFailure,
    LoadCompanyEmployeeDetailsSuccess,
    LoadCompanyEmployeeOverviewPage,
    LoadCompanyEmployeeOverviewPageFailure,
    LoadCompanyEmployeeOverviewPageSuccess,
    LoadEmployeeFilterCounts,
    LoadEmployeeFilterCountsFailure,
    LoadEmployeeFilterCountsSuccess,
    UpdateCompanyEmployee,
    UpdateCompanyEmployeeSuccess
} from '../actions/company-employee.actions';
import { DEFAULT_PAGE_SIZE } from '../../shared/constants-and-enums/pagination';
import { AppState } from '../store';
import { EmployeeOverviewView } from '@iota/inh-core-v1-management-client';

export interface CompanyEmployeeState extends EntityState<EmployeeOverviewView> {
    loading: boolean;
    actionLoading: boolean;
    actionError: any;
    error: any;
    loadedCompanies: string[];
    paginatedLoading: boolean;
    paginatedEmployees: EmployeeOverviewView[];
    totalElements: number;
    pageIndex: number;
    pageSize: number;
    currentCompanyId: string | null;
    lastOverviewPageParams: CompanyEmployeeOverviewPageParams | null;
    filterCounts: EmployeeFilterCounts | null;
    filterCountsLoading: boolean;
}

function selectCompanyEmployeeId(a: EmployeeOverviewView): string {
    return a.companyEmployeeId;
}

export const companyEmployeeAdapter: EntityAdapter<EmployeeOverviewView> = createEntityAdapter<EmployeeOverviewView>({
    selectId: selectCompanyEmployeeId
});

export const initialState: CompanyEmployeeState = companyEmployeeAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    actionError: undefined,
    error: undefined,
    loadedCompanies: [],
    paginatedLoading: false,
    paginatedEmployees: [],
    totalElements: 0,
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    currentCompanyId: null,
    lastOverviewPageParams: null,
    filterCounts: null,
    filterCountsLoading: false
});

export const companyEmployeeReducer = createReducer(
    initialState,
    on(LoadCompanyEmployeeDetailsByCompany, (state) => ({
        ...state,
        loading: true
    })),
    on(LoadCompanyEmployeeDetailsSuccess, (state, { employees, companyId }) =>
        companyEmployeeAdapter.upsertMany(
            Array.isArray(employees) ? employees : [],
            companyEmployeeAdapter.removeMany((s) => s.companyId === companyId, {
                ...state,
                loading: false,
                actionLoading: false,
                loadedCompanies: [...state.loadedCompanies, companyId]
            })
        )
    ),
    on(LoadCompanyEmployeeDetailsFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(CreateCompanyEmployee, UpdateCompanyEmployee, DeleteCompanyEmployee, (state) => ({
        ...state,
        actionLoading: true,
        actionError: undefined
    })),
    on(CreateCompanyEmployeeSuccess, UpdateCompanyEmployeeSuccess, (state) => ({
        ...state,
        actionLoading: false,
        actionError: undefined
    })),
    on(DeleteCompanyEmployeeSuccess, (state, { companyEmployeeId }) =>
        companyEmployeeAdapter.removeOne(companyEmployeeId, {
            ...state,
            actionLoading: false,
            actionError: undefined
        })
    ),

    on(CompanyEmployeeActionFailure, (state, { error }) => ({ ...state, actionLoading: false, actionError: error })),
    on(LoadCompanyEmployeeOverviewPage, (state, { params }) => ({
        ...state,
        paginatedLoading: true,
        lastOverviewPageParams: params
    })),
    on(LoadCompanyEmployeeOverviewPageSuccess, (state, { employees, totalElements, pageIndex, pageSize, companyId }) => ({
        ...state,
        paginatedLoading: false,
        paginatedEmployees: employees,
        totalElements,
        pageIndex,
        pageSize,
        currentCompanyId: companyId
    })),
    on(LoadCompanyEmployeeOverviewPageFailure, (state, { error }) => ({
        ...state,
        paginatedLoading: false,
        error
    })),
    on(LoadEmployeeFilterCounts, (state) => ({
        ...state,
        filterCountsLoading: true
    })),
    on(LoadEmployeeFilterCountsSuccess, (state, { counts }) => ({
        ...state,
        filterCountsLoading: false,
        filterCounts: counts
    })),
    on(LoadEmployeeFilterCountsFailure, (state, { error }) => ({
        ...state,
        filterCountsLoading: false,
        error
    }))
);

const { selectAll } = companyEmployeeAdapter.getSelectors();

export const companyEmployeeState = (state: AppState) => state.companyEmployeeState;
export const allCompanyEmployees = createSelector(companyEmployeeState, selectAll);
export const companyEmployeesByCompany = (companyId: string) =>
    createSelector(allCompanyEmployees, (employees) => employees.filter((elem) => elem.companyId === companyId));
export const companyEmployeeActionLoading = createSelector(companyEmployeeState, (state) => state.actionLoading);
export const companyEmployeeActionError = createSelector(companyEmployeeState, (state) => state.actionError);

export const companyEmployeePaginatedLoading = createSelector(companyEmployeeState, (state) => state.paginatedLoading);
export const companyEmployeePaginatedEmployees = createSelector(companyEmployeeState, (state) => state.paginatedEmployees);
export const companyEmployeeOverviewEmployeeById = (companyEmployeeId: string) =>
    createSelector(companyEmployeePaginatedEmployees, (employees) =>
        employees.find((employee) => employee.companyEmployeeId === companyEmployeeId)
    );
export const companyEmployeeTotalElements = createSelector(companyEmployeeState, (state) => state.totalElements);
export const companyEmployeePageIndex = createSelector(companyEmployeeState, (state) => state.pageIndex);
export const companyEmployeePageSize = createSelector(companyEmployeeState, (state) => state.pageSize);
export const companyEmployeeLastOverviewPageParams = createSelector(companyEmployeeState, (state) => state.lastOverviewPageParams);

export const companyEmployeeFilterCounts = createSelector(companyEmployeeState, (state) => state.filterCounts);
